import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Box, 
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
  Badge,
  Tooltip,
  alpha
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Dashboard as DashboardIcon, 
  MedicationOutlined as MedicationIcon, 
  Person as PersonIcon, 
  ExitToApp as LogoutIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  LocalHospital as HealthPracticeIcon,
  MedicalServices
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslations } from '../../translations/translations';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

function Navigation() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user, signOut } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  // Don't show navigation if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const menuItems = [
    { text: t.nav.dashboard, icon: <DashboardIcon />, path: '/' },
    // Temporarily hidden - will be enabled with role-based access control
    // { text: t.nav.chronicPrescriptions, icon: <MedicationIcon />, path: '/chronic-prescriptions' },
    // { text: t.nav.planOfCare, icon: <MedicalServices />, path: '/plan-of-care' },
    { text: t.nav.privatePractice, icon: <HealthPracticeIcon />, path: '/private-health-practice' },
    { text: t.nav.profile, icon: <PersonIcon />, path: '/profile' }
  ];

  const drawer = (
    <Box sx={{ width: 250, height: '100%', display: 'flex', flexDirection: 'column' }} role="presentation">
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img 
          src="/images/healin-logo.svg" 
          alt="Healin Logo" 
          style={{ 
            height: '40px', 
            width: 'auto',
            maxWidth: '180px',
            objectFit: 'contain'
          }} 
        />
      </Box>
      <Divider sx={{ mx: 2, opacity: 0.6 }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 1.5, 
          borderRadius: 2,
          mb: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.08)
        }}>
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40, 
              bgcolor: theme.palette.primary.main,
              mr: 2
            }}
          >
            {user?.username ? user.username.substring(0, 2).toUpperCase() : 'DR'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {user?.name || 'Doctor'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {user?.role || 'Healthcare Provider'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <List sx={{ px: 2, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem 
              button 
              key={item.text} 
              component={Link} 
              to={item.path}
              selected={isSelected}
              onClick={() => isMobile && setDrawerOpen(false)}
              sx={{
                mb: 1,
                py: 1.2,
                px: 1.5,
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.16),
                  },
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                  minWidth: 40
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? theme.palette.primary.main : theme.palette.text.primary
                }}
              />
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ mx: 2, opacity: 0.6 }} />
      <List sx={{ px: 2, pb: 2 }}>
        <ListItem 
          button
          sx={{
            mb: 1,
            py: 1.2,
            px: 1.5,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t.nav.settings} 
            primaryTypographyProps={{ 
              fontWeight: 500,
              color: theme.palette.text.primary
            }}
          />
        </ListItem>
        <ListItem 
          button
          onClick={handleLogout}
          sx={{
            py: 1.2,
            px: 1.5,
            '&:hover': {
              bgcolor: alpha(theme.palette.error.light, 0.12),
            },
          }}
        >
          <ListItemIcon sx={{ color: theme.palette.error.main, minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t.nav.logout} 
            primaryTypographyProps={{ 
              fontWeight: 500,
              color: theme.palette.error.main
            }}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="static" 
        color="default" 
        elevation={0}
        sx={{ 
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ height: 64 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700, 
              color: theme.palette.primary.main,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            <img 
              src="/images/healin-logo.svg" 
              alt="Healin Logo" 
              style={{ 
                height: '32px', 
                width: 'auto',
                maxWidth: '150px',
                objectFit: 'contain'
              }} 
            />
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LanguageSwitcher size="small" />
            <Tooltip title={t.nav.notifications}>
              <IconButton color="inherit" sx={{ ml: 1 }}>
                <Badge badgeContent={3} color="secondary">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Button 
              color="inherit" 
              component={Link} 
              to="/profile"
              startIcon={
                <Avatar 
                  sx={{ 
                    width: 28, 
                    height: 28, 
                    bgcolor: theme.palette.primary.main,
                    fontSize: '0.875rem'
                  }}
                >
                  {user?.username ? user.username.substring(0, 2).toUpperCase() : 'DR'}
                </Avatar>
              }
              sx={{ 
                ml: 1, 
                borderRadius: 4,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }
              }}
            >
              {user?.name || 'Doctor'}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? drawerOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 250,
            position: isMobile ? 'absolute' : 'fixed',
            height: isMobile ? '100%' : 'calc(100% - 64px)', // Subtract AppBar height
            top: isMobile ? 0 : 64, // AppBar height
            borderRight: 'none',
            boxShadow: isMobile ? '0px 4px 20px rgba(0, 0, 0, 0.1)' : 'none',
          },
          display: { xs: 'block', md: isMobile ? 'none' : 'block' }
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navigation; 